import { Stack, Typography } from "@mui/material";
import { useSetStardustParticipationMutation } from "Api/Mutations/Loyalty/useSetStardustParticipationMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import {
  ProgramParticipationSource,
  ProgramParticipationState,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";

const RESOURCES = Resources.Pages.Content.Stardust.Participation;

/**
 * Bottom of the Stardust page (ZS-1624): rendered only for a consenting person (the page shows the
 * consent dialog otherwise), so it offers one thing — leaving the program after a confirmation
 * that spells out the consequences. Leaving withdraws the consent the page needs, so it ends on
 * the dashboard instead of the consent dialog the person just declined.
 */
export const StardustParticipationSection = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { isOpen, open, handleResult } = useConfirmDialog();
  const { mutate: leaveProgram, isPending } =
    useSetStardustParticipationMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.OptedOutToast));
        navigate(appUrl("dashboard"));
      },
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });

  return (
    <PrimaryPaper>
      <Stack gap={3}>
        <Stack gap={1}>
          <PaperTitle>{t(RESOURCES.Title)}</PaperTitle>
          <Typography>{t(RESOURCES.OptedInStatus)}</Typography>
        </Stack>
        <PrimaryButton
          fullWidth
          variant="outlined"
          color="error"
          onClick={open}
          isLoading={isPending}
        >
          {t(RESOURCES.OptOutButton)}
        </PrimaryButton>
        <ConfirmDialog
          isOpen={isOpen}
          modalTitle={t(RESOURCES.OptOutDialogTitle)}
          modalContent={
            <Typography>{t(RESOURCES.OptOutDialogText)}</Typography>
          }
          confirmButtonText={t(RESOURCES.OptOutDialogConfirm)}
          isDestructive
          handleResult={handleResult(() =>
            leaveProgram({
              state: ProgramParticipationState.OptedOut,
              source: ProgramParticipationSource.ProgramSection,
            }),
          )}
        />
      </Stack>
    </PrimaryPaper>
  );
};
